import type { CalloutTone } from '@/shared/ui/callout'
import type { StatusTone } from '@/shared/ui/status-badge'

/**
 * Словарь доски «требует внимания». Вид сигнала — код ядра; оператору нужен
 * ответ на три вопроса сразу: что случилось, почему так и что делать. Ни один
 * сигнал ничего не закрывает и не сносит — конец заявке ставит только отзыв.
 */
export interface SignalKindMeta {
  title: string
  detail: string
  action: string
  tone: CalloutTone
  badge: StatusTone
}

const kinds: Record<string, SignalKindMeta> = {
  deficit: {
    title: 'Слотов не хватает',
    detail:
      'Окно заявки подошло, линии пула для неё есть, а готовых слотов меньше заказанного. Ядро добирает ёмкость каждым тиком.',
    action: 'Ждать тик либо прогреть руками из карточки заявки — ответ назовёт причину отказа.',
    tone: 'danger',
    badge: 'danger',
  },
  'waiting-lines': {
    title: 'Ждёт чужие линии',
    detail:
      'Гейт линий стоит перед деньгами: ни одного сервера под заявку не заказано, счётчик денег не тикает. Ожидание бесплатно.',
    action: 'Ничего чинить не нужно — линии освободит только отзыв заявки-держателя.',
    tone: 'info',
    badge: 'info',
  },
  overdue: {
    title: 'Окно прошло, отзыв не пришёл',
    detail:
      'Просрочка ничего не выключает: заявка продолжает звонить, держать линии и тратить деньги. Соседи по пулу стоят за её линиями.',
    action: 'Спросить потребителя и отозвать заявку, если кампания кончилась.',
    tone: 'warn',
    badge: 'warn',
  },
  idle: {
    title: 'Железо простаивает',
    detail:
      'Слоты заявки подняты и тарифицируются, а звонков по ним нет. Ядро само ничего не сносит: заявка куплена, и решать — потребителю.',
    action: 'Спросить потребителя, идёт ли кампания; ёмкость лишняя — отзыв.',
    tone: 'warn',
    badge: 'warn',
  },
  'lines-shrunk': {
    title: 'Линии ушли из-под живой заявки',
    detail:
      'Железо стоит и оплачивается, а звонить не на чем: учётку пула выключили, урезали каналы или пометили degraded. Честный слот — min(слоты железа, арендованные линии).',
    action: 'Телефония: учётки пула и их каналы — сначала линии, железо уже есть.',
    tone: 'danger',
    badge: 'danger',
  },
  'reports-lost': {
    title: 'Отчёты потеряны',
    detail:
      'Грейс досдачи вышел, юнит снесён вместе с неподтверждёнными отчётами. Минуты считаются только по ним — это выручка конца кампании, которой уже нет.',
    action:
      'Сверить usage кампании с ожиданием потребителя; повторить нечем — сигнал одноразовый и снимается рукой.',
    tone: 'danger',
    badge: 'danger',
  },
  'stuck-units': {
    title: 'Юнит застрял',
    detail:
      'Сторож пометил юнит error: он не добутстрапился или перестал докладывать, а деньги за карту идут.',
    action: 'Смотреть юнит: провайдер, образ, wg-адрес. Не чинится — удалить и заказать заново.',
    tone: 'danger',
    badge: 'danger',
  },
  'orphan-server': {
    title: 'Сервер без строки в реестре',
    detail:
      'У провайдера живёт оплаченная карта, за которой во флоте нет юнита: заказ идёт вне транзакции, и обрыв ответа оставляет её сиротой. Сама она не найдётся никогда.',
    action: 'Кабинет провайдера: сверить сервер со сканом аккаунта и погасить его руками.',
    tone: 'danger',
    badge: 'danger',
  },
}

/** Порядок фильтра доски — тот же перечень, что принимает `kind` у бэка. */
export const SIGNAL_KINDS = Object.keys(kinds)

/**
 * Вид сигнала словами. Незнакомый код честнее молчания: он значит «словарь
 * админки отстал от ядра», а не «ничего не случилось».
 */
export function signalKind(kind: string): SignalKindMeta {
  return (
    kinds[kind] ?? {
      title: `Сигнал «${kind || '—'}»`,
      detail: 'Вид сигнала админке незнаком — словарь отстал от ядра.',
      action: 'Смотреть структурный лог backend’а по субъекту сигнала.',
      tone: 'neutral',
      badge: 'muted',
    }
  )
}

const subjects: Record<string, string> = {
  reservation: 'Заявка',
  unit: 'Юнит',
  cell: 'Ячейка',
  pool: 'Пул',
  provider: 'Провайдер',
}

export const SIGNAL_SUBJECT_TYPES = Object.keys(subjects)

export function signalSubjectLabel(subjectType: string): string {
  return subjects[subjectType] ?? subjectType
}

export interface SignalLifecycle {
  label: string
  /** Кто снимет строку — единственное, чем сорта различаются в работе. */
  detail: string
  clearable: boolean
}

/**
 * Сорт сигнала. Выводимый стоит на живом условии: тик пересчитывает его каждым
 * прогоном и снимает сам, как только условия не стало, — руками он не
 * снимается, и кнопки у него быть не должно (409 у бэка тут дефект UI, а не
 * сценарий). Одноразовый описывает СЛУЧИВШЕЕСЯ: условия под ним нет,
 * пересчитать нечем, и убрать его может только человек, который разобрался.
 */
export function signalLifecycle(derived: boolean): SignalLifecycle {
  return derived
    ? {
        label: 'Выводимый',
        detail: 'Уйдёт сам, когда условия не станет — тик пересчитывает его каждым прогоном.',
        clearable: false,
      }
    : {
        label: 'Одноразовый',
        detail: 'Описывает случившееся: условия под ним нет — снимает оператор, разобравшись.',
        clearable: true,
      }
}
